import React, { useState } from 'react';
import {
  GetEventAccountDocument,
  GetEventAccountQuery,
} from '../../../../graphql/queries/getEventAccount.generated';
import { Link } from 'react-router-dom';
import {
  FaAngleDown,
  FaAngleRight,
  FaDownload,
  FaFileDownload,
  FaPlus,
  FaSave,
} from 'react-icons/fa';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';
import { useUpdateAuthorizedSignerMutation } from '../../../../graphql/mutations/updateAuthorizedSigner.generated';
import ItemLocationEditTable from '../../AuthorizedSigners/ItemLocationsEditTable';
import DownloadAuthorizedSignerPdf, {
  AUTHORIZED_SIGNER_QR_CODE_PREFIX,
} from '../../AuthorizedSigners/AuthorizedSignerPdf';
import { toCsvBlob } from '@/utils/data';
import { download } from '@/utils/download';
import { useCreateManyAuthorizedSignersMutation } from '../../../../graphql/mutations/createManyAuthorizedSigners.generated';

export function AuthorizedSignerTable(props: {
  eventAccount: GetEventAccountQuery['eventAccount'];
  creatingSigner: boolean;
  handleCreateSigner: () => void;
}) {
  const {
    eventAccount: eventAccountIn,
    creatingSigner,
    handleCreateSigner,
  } = props;
  const [openAuthorizedSigners, setOpenAuthorizedSigners] = useState<string[]>(
    [],
  );

  const [updateAuthorizedSigner, { loading: updatingAuthorizedSigner }] =
    useUpdateAuthorizedSignerMutation();
  const [eventAccount, setEventAccount] =
    useState<GetEventAccountQuery['eventAccount']>(eventAccountIn);

  const [showCreateManyAuthorizedSigners, setShowCreateManyAuthorizedSigners] =
    React.useState(false);

  const [
    createManyAuthorizedSignersAmount,
    setCreateManyAuthorizedSignersAmount,
  ] = React.useState('0');

  const [
    createManyAuthorizedSignersPrefix,
    setCreateManyAuthorizedSignersPrefix,
  ] = React.useState('New Authorized Signer');

  const [createManyAuthorizedSigners] =
    useCreateManyAuthorizedSignersMutation();

  function handleUpdateAuthorizedSigner(
    id: string,
    name: string,
    allowed_item_locations?: string[],
  ) {
    updateAuthorizedSigner({
      refetchQueries: ['GetEventAccount'],
      variables: {
        authorized_signer_id: id,
        name,
        allowed_item_location_ids: allowed_item_locations,
      },
    });
  }

  function toggleOpenAuthorizedSigners(id: string) {
    const index = openAuthorizedSigners.findIndex(
      (signerId) => signerId === id,
    );
    const newOpenAuthorizedSigners = [...openAuthorizedSigners];
    if (index >= 0) {
      newOpenAuthorizedSigners.splice(index, 1);
    } else {
      newOpenAuthorizedSigners.push(id);
    }
    setOpenAuthorizedSigners(newOpenAuthorizedSigners);
  }

  function isAuthorizedSignerOpen(id: string) {
    const index = openAuthorizedSigners.findIndex(
      (signerId) => signerId === id,
    );
    return index >= 0;
  }

  async function downloadCsv() {
    const authorizedSigners = eventAccount?.authorized_signers;
    if (!authorizedSigners) {
      return;
    }
    const csvObject = authorizedSigners.map((signer) => ({
      qrCode: `${AUTHORIZED_SIGNER_QR_CODE_PREFIX}${signer.id}`,
      name: signer.name,
      id: signer.id,
    }));

    const csvBlob = await toCsvBlob(csvObject);
    download(csvBlob, `authorized_signers_${eventAccount?.name}.csv`);
  }

  function toggleAllowedItemLocation(signerId: string, locationId: string) {
    if (eventAccount == null) return;

    const signer = eventAccount.authorized_signers.find(
      (s) => s.id === signerId,
    );
    if (!signer) return;

    const allowedLocations = signer.allowed_item_locations || [];
    const index = allowedLocations.findIndex((l) => l.id === locationId);
    if (index === -1) {
      allowedLocations.push({ id: locationId });
    } else {
      allowedLocations.splice(index, 1);
    }

    setEventAccount({
      ...eventAccount,
      authorized_signers: eventAccount.authorized_signers.map((s) =>
        s.id === signerId
          ? { ...s, allowed_item_locations: allowedLocations }
          : s,
      ),
    });
  }

  function getAllowedItemLocationNames(allowedItemLocationIds: string[]) {
    const allowedItemLocationNames = eventAccount?.item_locations
      ?.filter((loc) => allowedItemLocationIds?.some((id) => id === loc.id))
      .map((loc) => loc.name)
      .join(', ');
    return allowedItemLocationNames;
  }

  return (
    <div>
      <h4 className="mt-4 mb-4">Authorized Signers</h4>
      <div className="text-end">
        <Button
          className="mt-2 mb-2"
          onClick={() => setShowCreateManyAuthorizedSigners(true)}
        >
          <FaPlus />
          {' ' + 'Create Many'}
        </Button>
        <Button className="ms-2 mt-2 mb-2" onClick={downloadCsv}>
          <FaFileDownload />
          Download CSV
        </Button>
        <Button
          className="ms-2 mt-2 mb-2"
          disabled={(eventAccount?.authorized_signers || []).length === 0}
          onClick={() =>
            DownloadAuthorizedSignerPdf(
              eventAccount?.authorized_signers || [],
              eventAccount?.name || undefined,
            )
          }
        >
          <FaDownload />
          Download QR-Codes
        </Button>
        <Button
          className="ms-2 mt-2 mb-2"
          disabled={(eventAccount?.authorized_signers || []).length === 0}
          onClick={() =>
            (eventAccount?.authorized_signers || []).forEach((signer) => {
              DownloadAuthorizedSignerPdf([signer], signer?.name || undefined);
            })
          }
        >
          <FaDownload />
          {' ' + 'Download QR-Codes (separate)'}
        </Button>
      </div>
      <Table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Allowed Item Locations</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {[...(eventAccount?.authorized_signers || [])]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((signer) => [
              <tr key={`${eventAccount?.id}-${signer.id}`}>
                <td>
                  <Link to={'/authorized-signer/' + signer.id}>
                    {signer.name}
                  </Link>
                </td>
                <td>
                  {getAllowedItemLocationNames(
                    signer.allowed_item_locations?.map((loc) => loc.id) || [],
                  )}
                </td>
                <td>
                  <Button
                    className="border-0"
                    onClick={() => toggleOpenAuthorizedSigners(signer.id)}
                  >
                    {isAuthorizedSignerOpen(signer.id) && <FaAngleDown />}
                    {!isAuthorizedSignerOpen(signer.id) && <FaAngleRight />}
                  </Button>
                </td>
              </tr>,
              isAuthorizedSignerOpen(signer.id) ? (
                <tr key={`${eventAccount?.id}-${signer.id}-details`}>
                  <td colSpan={2}>
                    <ItemLocationEditTable
                      allowedLocationIds={
                        signer.allowed_item_locations?.map((loc) => loc.id) ||
                        []
                      }
                      onChange={(locationId) =>
                        toggleAllowedItemLocation(signer.id, locationId)
                      }
                      itemLocations={
                        (eventAccount?.item_locations as any) || []
                      }
                      toggleAll={() => {}}
                    />
                    <Button
                      disabled={updatingAuthorizedSigner}
                      onClick={() =>
                        handleUpdateAuthorizedSigner(
                          signer.id,
                          signer.name,
                          signer.allowed_item_locations?.map((loc) => loc.id),
                        )
                      }
                      className="border-0"
                    >
                      <FaSave /> {` Save Authorized Signer ${signer.name}`}
                    </Button>
                  </td>
                </tr>
              ) : null,
            ])}
          <tr>
            <td colSpan={2} className="border-0">
              <Button disabled={creatingSigner} onClick={handleCreateSigner}>
                <FaPlus /> {' Create Signer'}
              </Button>
            </td>
          </tr>
        </tbody>
      </Table>
      <Modal
        isOpen={showCreateManyAuthorizedSigners}
        onClose={() => setShowCreateManyAuthorizedSigners(false)}
      >
        <ModalHeader>Create many authorized signers</ModalHeader>
        <ModalBody>
          <div className="input-group mb-3">
            <div className="input-group-prepend">
              <span className="input-group-text" id="basic-addon1">
                Amount
              </span>
            </div>
            <input
              type="text"
              className="form-control"
              onChange={(e) =>
                setCreateManyAuthorizedSignersAmount(
                  e.target.value.replace(/[^0-9.]/g, ''),
                )
              }
              value={createManyAuthorizedSignersAmount}
            />
          </div>
          <div className="input-group mb-3">
            <div className="input-group-prepend">
              <span className="input-group-text" id="basic-addon1">
                Prefix
              </span>
            </div>
            <input
              type="text"
              className="form-control"
              onChange={(e) =>
                setCreateManyAuthorizedSignersPrefix(e.target.value)
              }
              value={createManyAuthorizedSignersPrefix}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={() => setShowCreateManyAuthorizedSigners(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={parseInt(createManyAuthorizedSignersAmount) <= 0}
            onClick={() => {
              if (eventAccount != null) {
                createManyAuthorizedSigners({
                  variables: {
                    event_account_id: eventAccount?.id,
                    prefix: createManyAuthorizedSignersPrefix,
                    amount: parseInt(createManyAuthorizedSignersAmount),
                  },
                  refetchQueries: [
                    {
                      query: GetEventAccountDocument,
                      variables: { id: eventAccount.id },
                    },
                  ],
                });
                setShowCreateManyAuthorizedSigners(false);
                setCreateManyAuthorizedSignersAmount('0');
                setCreateManyAuthorizedSignersPrefix('New Authorized Signer');
              }
            }}
          >
            Create
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
